import React from "react";
import { CheckCircle2, CheckSquare, Plus, Square, X } from "lucide-react";
import CustomDatePicker from "../../components/CustomDatePicker";
import CustomTimePicker from "../../components/CustomTimePicker";

export default function ScheduleFormFields({
  availableTypes,
  formData,
  getTypeButtonClass,
  handleAddChecklist,
  handleRemoveChecklist,
  handleToggleChecklist,
  isMobile,
  newChecklistInput,
  setFormData,
  setNewChecklistInput,
  theme,
}) {
  return (
    <>
      <div>
        <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">일정 제목</label>
        <input
          className={`w-full border-2 border-[#EAEAEA] p-3.5 rounded-2xl bg-[#F9F9F8] focus:bg-white focus:${theme.border} outline-none transition font-bold text-gray-700`}
          placeholder="예: 여름 수련회"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
      </div>

      <div>
        <label className="text-xs font-bold text-gray-500 mb-2 block ml-1">중요도</label>
        <div className="flex gap-2 bg-[#F9F9F8] p-1 rounded-xl border border-[#EAEAEA]">
          {["high", "medium", "low"].map((priority) => (
            <button
              key={priority}
              type="button"
              onClick={() => setFormData({ ...formData, priority })}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${formData.priority === priority
                ? (priority === "high" ? "bg-red-500 text-white shadow-md" : priority === "medium" ? `${theme.bg} text-white shadow-md` : "bg-gray-500 text-white shadow-md")
                : "text-gray-400 hover:bg-white"
                }`}
            >
              {priority === "high" ? "긴급" : priority === "medium" ? "보통" : "여유"}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-1.5">
          <label className="text-xs font-bold text-gray-500 block ml-1">날짜</label>
          <label className="flex items-center gap-1.5 cursor-pointer" onClick={() => setFormData((prev) => ({ ...prev, endDate: prev.endDate ? "" : prev.date }))}>
            <div className={`w-4 h-4 rounded border flex items-center justify-center transition ${formData.endDate ? `${theme.bg} border-transparent` : "border-gray-300 bg-white"}`}>
              {formData.endDate && <CheckSquare size={10} className="text-white" />}
            </div>
            <span className="text-xs font-bold text-gray-400">종료일 설정</span>
          </label>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <CustomDatePicker theme={theme} value={formData.date} onChange={(date) => setFormData({ ...formData, date })} className="w-full" />
          </div>
          {formData.endDate && (
            <>
              <span className="self-center text-gray-300 font-bold">~</span>
              <div className="flex-1">
                <CustomDatePicker theme={theme} value={formData.endDate} onChange={(date) => setFormData({ ...formData, endDate: date })} className="w-full" />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">시작 시간</label>
          <CustomTimePicker theme={theme} value={formData.startTime} onChange={(time) => setFormData({ ...formData, startTime: time })} className="w-full" />
        </div>
        <div className="flex-1">
          <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">종료 시간</label>
          <CustomTimePicker theme={theme} value={formData.endTime} onChange={(time) => setFormData({ ...formData, endTime: time })} className="w-full" />
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-gray-500 mb-2 block ml-1">유형</label>
        <div className="flex gap-2 flex-wrap">
          {availableTypes.filter((type) => type.id !== "all").map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setFormData({ ...formData, type: type.id })}
              className={getTypeButtonClass(type.id, formData.type === type.id)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">체크리스트 (할 일)</label>
        <div className="flex gap-2 mb-2">
          <input
            className={`flex-1 border-2 border-[#EAEAEA] p-2.5 rounded-xl bg-[#F9F9F8] focus:bg-white focus:${theme.border} outline-none transition text-sm font-medium`}
            placeholder="할 일 추가..."
            value={newChecklistInput}
            onChange={(e) => setNewChecklistInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddChecklist())}
          />
          <button type="button" onClick={handleAddChecklist} className={`p-2.5 ${theme.bg} text-white rounded-xl hover:opacity-90 transition`}>
            <Plus size={18} />
          </button>
        </div>
        <div className={`space-y-2 ${isMobile ? "" : "max-h-32 overflow-y-auto custom-scrollbar"}`}>
          {formData.checklists.map((item) => (
            <div key={item.id} className="flex items-center justify-between bg-gray-50 p-2.5 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => handleToggleChecklist(item.id)}>
                {item.checked ? <CheckCircle2 size={16} className={theme.text} /> : <Square size={16} className="text-gray-300" />}
                <span className={`text-sm ${item.checked ? "text-gray-400 line-through" : "text-gray-700"}`}>{item.text}</span>
              </div>
              <button type="button" onClick={() => handleRemoveChecklist(item.id)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">장소</label>
        <input
          className={`w-full border-2 border-[#EAEAEA] p-3.5 rounded-2xl bg-[#F9F9F8] focus:bg-white focus:${theme.border} outline-none transition font-bold text-gray-700`}
          placeholder="예: 대예배실"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
      </div>

      <div>
        <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">메모</label>
        <textarea
          className={`w-full border-2 border-[#EAEAEA] p-3.5 rounded-2xl bg-[#F9F9F8] focus:bg-white focus:${theme.border} outline-none transition resize-none font-medium text-gray-700 h-24`}
          rows="3"
          placeholder="상세 내용을 적어주세요"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
      </div>
    </>
  );
}
